import { IconHide, SecondaryButton, Space } from '@procurify/ui'

import { TextLoader } from '@webapp/core/components/ContentLoader'
import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  SpendingCardRegion,
  type SpendingCardRegionEnum,
} from '@webapp/modules/spending-cards/entities/enums/SpendingCardRegionEnum'
import messages from './messages'
import { PaymentDetailsCardStyled } from './styles/PaymentDetailsCardStyled'

// tslint:disable-next-line: no-var-requires
// tslint:disable-next-line: no-var-requires
const visaBusinessSVG = require('!svg-url-loader!./assets/visa-logo-business.svg')
const visaCommercialSVG = require('!svg-url-loader!./assets/visa-logo-commercial.svg')

export interface IPaymentDetailsCardProps {
  last4Digits: string
  balance: string
  currency: CurrencyName
  onRevealPaymentDetails: () => void
  onHidePaymentDetails: () => void
  iframeUrl: string | undefined
  ariaLabel?: string
  isLoading?: boolean
  region: SpendingCardRegionEnum
}

export const PaymentDetailsCard = ({
  last4Digits,
  balance,
  currency,
  onRevealPaymentDetails,
  onHidePaymentDetails,
  iframeUrl,
  ariaLabel,
  isLoading,
  region,
}: IPaymentDetailsCardProps) => {
  const intl = useProcurifyIntl()

  const showPaymentDetailsIframe = !!iframeUrl
  const showLoader = isLoading
  const showMaskedNumber = !showPaymentDetailsIframe && !showLoader

  const renderVisaLogo = () => {
    if (region === SpendingCardRegion.CA) return null

    return (
      <img
        alt={intl.formatMessage(messages.visaLogoAltText)}
        className='network-logo'
        src={
          region === SpendingCardRegion.UK ? visaBusinessSVG : visaCommercialSVG
        }
        width='100%'
      />
    )
  }

  return (
    <PaymentDetailsCardStyled
      aria-label={
        ariaLabel ||
        intl.formatMessage(messages.cardAriaLabel, {
          cardNumber: last4Digits,
        })
      }
    >
      <Space direction='horizontal' justify='space-between'>
        <Price
          price={balance}
          currency={currency}
          aria-label={intl.formatMessage(messages.availableBalanceLabel)}
        />

        {showPaymentDetailsIframe ? (
          <SecondaryButton
            onClick={onHidePaymentDetails}
            iconComponent={<IconHide />}
          >
            {intl.formatMessage(messages.hideDetailsButtonText)}
          </SecondaryButton>
        ) : (
          <SecondaryButton
            disabled={isLoading}
            onClick={onRevealPaymentDetails}
            icon='visible'
          >
            {intl.formatMessage(messages.showDetailsButtonText)}
          </SecondaryButton>
        )}
      </Space>

      {showLoader && (
        <Space>
          <TextLoader width={160} inverted />
          <Space direction='horizontal'>
            <TextLoader width={120} inverted />
            <TextLoader width={120} inverted />
          </Space>
        </Space>
      )}

      {showPaymentDetailsIframe && (
        <iframe
          aria-label={intl.formatMessage(messages.paymentDetailsLabel)}
          src={iframeUrl}
        />
      )}

      {showMaskedNumber && (
        <Space direction='horizontal' justify='space-between' align='center'>
          <span>•••• {last4Digits}</span>

          {renderVisaLogo()}
        </Space>
      )}
    </PaymentDetailsCardStyled>
  )
}
